<template>
  <div>
    <div v-if="summaryView" class="flex flex-wrap md:flex-row align-middle items-center justify-center">
      <loader v-if="loading" class="flex-grow text-center" />
      <div v-else class="flex-grow text-center">
        <div v-if="excludedAgentCount !== null"><span class="font-black">{{ excludedAgentCount }}</span> Excluded {{ excludedAgentCount === 1 ? 'Agent' : 'Agents' }}</div>
        <div v-if="blacklistedPropertyCount !== null"><span class="font-black">{{ blacklistedPropertyCount }}</span> Blacklisted {{ blacklistedPropertyCount === 1 ? 'Property' : 'Properties' }}</div>
      </div>
      <div class="text-center my-2">
        <button @click="$emit('show')" class="bg-tertiary hover:bg-primary text-white py-3 px-8 text-sm uppercase rounded shadow">Blacklist/Exclusions</button>
      </div>
    </div>
    <div v-else>
      <hr class="mt-2 mb-6 border-bottom w-full">
      <div class="flex flex-wrap md:flex-row align-middle items-center justify-center mb-3">
        <div class="flex-grow my-2">
          <h1 slot="header" class="text-xl">
            Auto Prospecting Blacklist
          </h1>
        </div>
        <div class="text-center">
          <button type="button" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-3 px-6 mb-3 text-sm uppercase border border-grey hover:border-transparent rounded" @click="$emit('hide')">&lt; Back to Manage Data</button>
        </div>
      </div>
      <p class="mb-6">We will never send mail to an address on your blacklist, or to properties listed by an agent that you've excluded.</p>
      <div class="flex flex-wrap md:flex-row align-middle items-center mb-6">
        <div class="bg-white border w-full md:w-1/3 mb-6 md:mb-0 px-3 flex items-center rounded overflow-hidden">
          <input v-model="searchInBlacklist" type="text" class="py-4 px-2 w-full" :placeholder="searchPlaceholder" />
          <button>
            <span class="w-auto flex justify-end text-grey p-1">
              <i class="fa fa-search text-grey"></i>
            </span>
          </button>
        </div>
        <div class="mb-6 mr-4 md:mb-0 md:mr-0 md:ml-4">
          <button @click.prevent="showBlacklistPropertyModal = true" class="bg-tertiary hover:bg-primary text-white py-3 px-8 text-sm uppercase rounded shadow">
            <i class="fa fa-home mr-1" /> Blacklist a Property
          </button>
        </div>
        <div class="mb-6 md:mb-0 md:ml-4">
          <button @click.prevent="showExcludeAgentModal = true" class="bg-tertiary hover:bg-primary text-white py-3 px-8 text-sm uppercase rounded shadow">
            <i class="fa fa-sign mr-1" /> Exclude an Agent
          </button>
        </div>
      </div>
      <modal
        width="70vw"
        v-if="showExcludeAgentModal !== false"
        @leave="showExcludeAgentModal = false"
        show-close-btn
        class="leading-normal"
      >
        <h2 class="font-normal mb-4">Exclude an Agent</h2>
        <p class="">Select the agents to exclude from the list below.</p>
        <p class="">These are all known agents operating in your territory (active postcodes).</p>
        <div v-if="manualExcludeAgent">
          <p class="mt-4">Please enter the agent name to exclude. Listings will be excluded where the agent name is an <strong>exact match</strong> only.</p>
          <loader v-if="loadingManualExcludeAgent" class="my-3" />
          <div v-else class="flex flex-row items-center mt-4 mb-6 ">
            <div class="bg-white border flex flex-grow mr-3 px-3 items-center rounded overflow-hidden">
              <input v-model="manualExcludeAgentText" type="text" class="py-4 px-2 w-full" placeholder="Enter Agent Name" />
            </div>
            <div class="">
              <button @click.prevent="manualExcludeSingleAgent()" class="bg-tertiary hover:bg-primary text-white py-4 px-8 text-sm uppercase rounded shadow">
                <i class="fa fa-sign mr-1" /> Exclude Agent
              </button>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="bg-white border md:w-3/4 w-full mt-6 mb-6 px-3 flex items-center rounded overflow-hidden">
            <input v-model="excludeAgentSearch" type="text" class="py-4 px-2 w-full" placeholder="Search Agent Name" />
            <button @click="getExcludeAgentOptions()">
              <span class="w-auto flex justify-end text-grey p-1">
                <i class="fa fa-search text-grey"></i>
              </span>
            </button>
          </div>
          <loader v-if="excludeAgentOptions === null" />
          <div v-else>
            <div v-if="excludeAgentOptions.length > 0" class="max-h-80 overflow-y-scroll">
              <table class="w-full text-left">
                <thead>
                  <tr class="uppercase">
                    <th class="px-4 pb-2 font-normal">Agent Name</th>
                    <th class="px-4 pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(agent, i) in excludeAgentOptions" :key="i" class="bg-grey-lighter text-xs md:text-sm">
                    <td class="p-4">{{ agent.name }}</td>
                    <td class="p-4 text-center">
                      <input type="checkbox" v-model="agent.excluded" :id="`blacklist-agent-${i}`" :disabled="agent.already_excluded">
                      <label :for="`blacklist-agent-${i}`"></label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!manualExcludeAgent" class="mt-3 text-center underline hover:text-blue-dark text-sm cursor-pointer" @click="manualExcludeAgent = true">
              Can't find the agent?
            </div>
            <div class="mt-6 mb-4 text-center">
              <button @click.prevent="excludeMultipleAgents" class="bg-tertiary hover:bg-primary text-white py-3 px-8 text-sm uppercase rounded shadow">
                <i class="fa fa-sign mr-1" /> Exclude Agents
              </button>
            </div>
          </div>
        </div>
      </modal>
      <blacklist-property-search
        v-if="showBlacklistPropertyModal"
        v-model="searchAddress"
        @saved="savedBlacklistPropertySearch"
        @leave="showBlacklistPropertyModal = false"
      />
      <panel class="pt-6">
        <loader v-if="loading || reloading" />
        <div class="w-full overflow-x-scroll" v-else>
          <div class="flex flex-wrap lg:flex-row w-full align-middle items-center border-bottom mb-8 py-4">
            <div class="text-xl pr-6 border-r border-grey-darkest" :class="showAgentsBlacklist ? 'text-grey cursor-pointer hover:underline' : 'text-grey-darkest'" @click="showAgentsBlacklist = false">Blacklisted Properties</div>
            <div class="text-xl px-6" :class="showAgentsBlacklist ? 'text-grey-darkest' : 'text-grey cursor-pointer hover:underline'" @click="showAgentsBlacklist = true">Excluded Agents</div>
            <div class="flex-grow text-center">
              <div class="text-sm">Results</div>
              <div v-if="showAgentsBlacklist"><span class="font-black">{{ excludedAgentCount }}</span> Excluded {{ excludedAgentCount === 1 ? 'Agent' : 'Agents' }}</div>
              <div v-else><span class="font-black">{{ blacklistedPropertyCount }}</span> Blacklisted {{ blacklistedPropertyCount === 1 ? 'Property' : 'Properties' }}</div>
            </div>
          </div>
          <div v-if="showAgentsBlacklist">
            <p v-if="excludedAgentCount === 0">There are currently no excluded agents. Please exclude your own brand(s).</p>
            <table v-else class="w-full text-left">
              <thead>
                <tr class="text-sm uppercase">
                  <th class="px-4 pb-4 cursor-pointer" @click="sortBy('agent_name')">
                    Agent
                    <i v-if="sorting['agent_name'] === 'asc'" class="fa fa-sort-down cursor-pointer"></i>
                    <i v-else-if="sorting['agent_name'] === 'desc'" class="fa fa-sort-up cursor-pointer"></i>
                    <i v-else class="fa fa-sort cursor-pointer"></i>
                  </th>
                  <th class="px-4 pb-4 cursor-pointer" @click="sortBy('created_at')">
                    Date Excluded
                    <i v-if="sorting['created_at'] === 'asc'" class="fa fa-sort-down cursor-pointer"></i>
                    <i v-else-if="sorting['created_at'] === 'desc'" class="fa fa-sort-up cursor-pointer"></i>
                    <i v-else class="fa fa-sort cursor-pointer"></i>
                  </th>
                  <th class="px-4 pb-4 text-center">Re-Instate</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isFineAndCountry" class="bg-grey-lighter text-xs md:text-sm">
                  <td class="p-4 border-l-4 border-grey-darker">Fine &amp; Country (All)</td>
                  <td class="p-4">-</td>
                  <td class="p-4 border-r-4 border-grey-darker text-center">
                    N/A
                  </td>
                </tr>
                <tr v-for="agent in excludedAgents.data" :key="agent.id" class="bg-grey-lighter text-xs md:text-sm">
                  <td class="p-4 border-l-4 border-grey-darker">{{ agent.agent_name }}</td>
                  <td class="p-4">{{ agent.created_at | date }}</td>
                  <td class="p-4 border-r-4 border-grey-darker text-lg text-center" title="Remove from blacklist">
                    <loader v-if="agent.id === reinstateAgentId" />
                    <div v-else @click="removeAgentFromBlacklist(agent.id)" class="cursor-pointer text-grey-darker hover:text-green"><i class="fas fa-check-circle"></i></div>
                  </td>
                </tr>
              </tbody>
            </table>
            <paginator v-if="excludedAgents" :data="excludedAgents" @change="blacklistPageChanged" />
          </div>
          <div v-else>
            <p v-if="blacklistedPropertyCount === 0">There are currently no blacklisted properties.</p>
            <table v-else class="w-full text-left">
              <thead>
                <tr class="text-sm uppercase">
                  <th class="px-4 pb-4 cursor-pointer" @click="sortBy('address_line1')">
                    Address
                    <i v-if="sorting['address_line1'] === 'asc'" class="fa fa-sort-down cursor-pointer"></i>
                    <i v-else-if="sorting['address_line1'] === 'desc'" class="fa fa-sort-up cursor-pointer"></i>
                    <i v-else class="fa fa-sort cursor-pointer"></i>
                  </th>
                  <th class="px-4 pb-4 cursor-pointer" @click="sortBy('address_postcode')">
                    Postcode
                    <i v-if="sorting['address_postcode'] === 'asc'" class="fa fa-sort-down cursor-pointer"></i>
                    <i v-else-if="sorting['address_postcode'] === 'desc'" class="fa fa-sort-up cursor-pointer"></i>
                    <i v-else class="fa fa-sort cursor-pointer"></i>
                  </th>
                  <th class="px-4 pb-4 cursor-pointer" @click="sortBy('created_at')">
                    Date Blacklisted
                    <i v-if="sorting['created_at'] === 'asc'" class="fa fa-sort-down cursor-pointer"></i>
                    <i v-else-if="sorting['created_at'] === 'desc'" class="fa fa-sort-up cursor-pointer"></i>
                    <i v-else class="fa fa-sort cursor-pointer"></i>
                  </th>
                  <th class="pb-4 text-center">Re-Instate</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="property in blacklistData.data" :key="property.id" class="bg-grey-lighter text-xs md:text-sm">
                  <td class="p-4 border-l-4 border-grey-darker uppercase">{{ [property.address_line1, property.address_line2, property.address_line3, property.address_town, property.address_line4].filter(Boolean).join(', ') }}</td>
                  <td class="p-4">{{ property.address_postcode }}</td>
                  <td class="p-4">{{ property.created_at }}</td>
                  <td class="p-4 border-r-4 border-grey-darker text-lg text-center" title="Remove from blacklist">
                    <loader v-if="property.id === reinstatePropertyId" />
                    <div v-else @click="removeFromBlacklist(property)" class="cursor-pointer text-grey-darker hover:text-green"><i class="fas fa-check-circle"></i></div>
                  </td>
                </tr>
              </tbody>
            </table>
            <paginator v-if="blacklistData" :data="blacklistData" @change="blacklistPageChanged" />
          </div>
        </div>
      </panel>
    </div>
  </div>
</template>

<script>
import api from '@/http'
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import { debounce, find } from 'lodash'
import BlacklistPropertySearch from './BlacklistPropertySearch.vue'

export default {
  name: 'ApsBlacklist',
  components: { BlacklistPropertySearch },
  props: {
    summaryView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      reloading: false,
      blacklistData: null,
      excludedAgents: {},
      page: 1,
      showAgentsBlacklist: false,
      showBlacklistPropertyModal: false,
      showExcludeAgentModal: false,
      excludeAgentOptions: null,
      excludeAgentSearch: null,
      blacklistPropertySearch: null,
      searchInBlacklist: null,
      searchAddress: {
        address_line1: null,
        address_line2: null,
        address_line3: null,
        address_town: null,
        address_line4: null,
        address_postcode: null
      },
      sorting: {},
      reinstatePropertyId: null,
      reinstateAgentId: null,
      manualExcludeAgent: false,
      manualExcludeAgentText: null,
      loadingManualExcludeAgent: false
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'system']),
    isFineAndCountry () {
      return this.system.networks[0].id === 7 || this.system.networks[0].id === 8
    },
    blacklistedPropertyCount () {
      if (!this.blacklistData || !this.blacklistData.total) {
        return null
      }

      return this.blacklistData.total
    },
    excludedAgentCount () {
      if (!this.excludedAgents || !this.excludedAgents.total) {
        return null
      }

      let count = this.excludedAgents.total

      if (this.isFineAndCountry) {
        count++
      }

      return count
    },
    excludedAgentSelections () {
      if (!this.excludeAgentOptions || this.excludeAgentOptions.length < 1) {
        return []
      }
      return this.excludeAgentOptions.filter(agent => agent.excluded && !agent.already_excluded)
    },
    searchPlaceholder () {
      if (this.showAgentsBlacklist) {
        return 'Search Agent Name'
      }

      return 'Search Address / Postcode'
    }
  },
  created () {
    this.setDefaultSort()
    Promise.all([
      this.getBlacklistedProperties(),
      this.getExcludedAgents()
    ]).then(() => {
      this.loading = false
    })
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    blacklistPageChanged (page) {
      this.page = page
    },
    getBlacklistedProperties () {
      let params = {
        office_id: this.selectedOffice.id,
        page: this.page,
        order_by: Object.keys(this.sorting).length > 0 ? Object.keys(this.sorting)[0] : null,
        sort: Object.values(this.sorting).length > 0 ? Object.values(this.sorting)[0] : null
      }
      if (this.searchInBlacklist && this.searchInBlacklist.length > 0) {
        params.q = this.searchInBlacklist
      }
      api.get('/aps/blacklist', {
        params: params
      }).then(response => {
        for (let i = 0; i < response.data.data.length; i++) {
          response.data.data[i].created_at = moment(response.data.data[i].created_at).format('DD/MM/YYYY')
        }
        this.blacklistData = response.data
        this.reloading = false
        this.reinstatePropertyId = null
      })
    },
    getExcludedAgents () {
      let params = {
        office_id: this.selectedOffice.id,
        page: this.page,
        order_by: Object.keys(this.sorting).length > 0 ? Object.keys(this.sorting)[0] : null,
        sort: Object.values(this.sorting).length > 0 ? Object.values(this.sorting)[0] : null
      }
      if (this.searchInBlacklist && this.searchInBlacklist.length > 0) {
        params.q = this.searchInBlacklist
      }
      api.get('/aps/exclusions', {
        params: params
      }).then(response => {
        this.excludedAgents = response.data.exclusions
        this.reloading = false
        this.reinstateAgentId = null
      })
    },
    getExcludeAgentOptions: debounce(function () {
      let params = {
        office_id: this.selectedOffice.id
      }
      if (this.excludeAgentSearch && this.excludeAgentSearch.length > 0) {
        params.q = this.excludeAgentSearch
      }
      api.get('/aps/exclusions/options-list', {
        params: params
      }).then(response => {
        this.excludeAgentOptions = response.data.data
        this.excludeAgentOptions.forEach(agent => {
          agent.already_excluded = agent.excluded
        })
      })
    }, 500),
    reload () {
      this.reloading = true
      if (this.showAgentsBlacklist) {
        this.getExcludedAgents()
      } else {
        this.getBlacklistedProperties()
      }
    },
    sortBy (columnName) {
      if (this.sorting[columnName]) {
        let direction = this.sorting[columnName]
        if (direction === 'asc') {
          // it was sorting asc, so change it to sort desc
          let newSort = {}
          newSort[columnName] = 'desc'
          this.sorting = newSort
        } else {
          // it was sorting desc, don't sort with this field now
          this.sorting[columnName] = ''
          delete this.sorting[columnName]
        }
      } else {
        // it wasn't sorting, sort it asc
        let newSort = {}
        newSort[columnName] = 'asc'
        this.sorting = newSort
      }
      // re-fetch the data with new sort order
      this.page = 1
      this.reload()
    },
    savedBlacklistPropertySearch ($event) {
      if ($event.existing) {
        // Property was already blacklisted
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Property is already Blacklisted',
          info: 'Please note this property has already been added to your blacklist. If you require any assistance, please contact The Support Team.'
        })
      }
    },
    isExcludedAgent (agent) {
      return find(this.excludedAgents, (excludedAgent) => excludedAgent.agent_name === agent)
    },
    excludeSingleAgent (agentName) {
      api.post('/aps/exclusions', {
        office_id: this.selectedOffice.id,
        agent_name: agentName
      })
    },
    manualExcludeSingleAgent () {
      this.loadingManualExcludeAgent = true
      api.post('/aps/exclusions', {
        office_id: this.selectedOffice.id,
        agent_name: this.manualExcludeAgentText
      }).then(response => {
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Agent Excluded',
          info: 'The list of excluded agents has been updated.'
        })
        this.showExcludeAgentModal = false
        this.manualExcludeAgent = false
        this.manualExcludeAgentText = null
        this.loadingManualExcludeAgent = false
        this.reload()
      })
    },
    excludeMultipleAgents () {
      let requests = []
      if (!this.excludedAgentSelections || this.excludedAgentSelections.length < 1) {
        this.showExcludeAgentModal = false
        return null
      }
      this.excludedAgentSelections.forEach(agent => {
        requests.push(this.excludeSingleAgent(agent.name))
      })
      Promise.all(requests)
        .then(() => {
          this.reload()
          this.showExcludeAgentModal = false

          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Agent Exclusions Updated',
            info: 'The list of excluded agents has been updated.'
          })
        })
    },
    removeFromBlacklist (property) {
      this.reinstatePropertyId = property.id
      api.delete(`/aps/blacklist-property/${property.id}`).then(response => {
        let address = [property.address_line1, property.address_line2, property.address_line3, property.address_town, property.address_line4, property.address_postcode].filter(Boolean).join(', ')
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Property Whitelisted',
          info: address
        })
        this.searchInBlacklist = null
        this.reload()
      })
    },
    removeAgentFromBlacklist (agentId) {
      this.reinstateAgentId = agentId
      api.delete(`/aps/exclusions/${agentId}`).then(response => {
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Agent Re-Instated',
          info: 'Please re-include individual properties as required.'
        })
        this.searchInBlacklist = null
        this.reload()
      })
    },
    setDefaultSort () {
      if (this.showAgentsBlacklist) {
        this.sorting = {
          agent_name: 'asc'
        }
      } else {
        this.sorting = {
          created_at: 'desc'
        }
      }
    },
    resetSearchAddress () {
      this.searchAddress = {
        address_line1: null,
        address_line2: null,
        address_line3: null,
        address_town: null,
        address_line4: null,
        address_postcode: null
      }
    }
  },
  watch: {
    selectedOffice () {
      this.reload()
    },
    page () {
      if (!this.page) {
        return
      }

      this.reload()
    },
    searchInBlacklist () {
      this.reload()
    },
    showAgentsBlacklist () {
      this.page = 1
      this.searchInBlacklist = null
      this.setDefaultSort()
      this.reload()
    },
    showBlacklistPropertyModal (value) {
      if (!value) {
        this.reload()
      }

      this.resetSearchAddress()
    },
    showExcludeAgentModal (value) {
      if (!value) {
        this.reload()
        return false
      }

      this.manualExcludeAgent = false
      this.excludeAgentOptions = null
      this.excludeAgentSearch = null
      this.getExcludeAgentOptions()
    },
    excludeAgentSearch () {
      this.excludeAgentOptions = null
      this.getExcludeAgentOptions()
    }
  }
}
</script>

<style lang="scss" scoped>
table {
  border-collapse: separate;
  border-spacing: 0 4px;
}
</style>
